import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  createLead,
  getLeadErrorMessage,
  readLead,
  readLeadDuplicateId,
} from '../../services/leadsService';
import { isPlausibleEmail, isPlausiblePhone } from '../leadsConfig';
import LeadChannelPicker from './LeadChannelPicker';
import InfoNotice from '../../Components/InfoNotice';
import { sanitizePhoneInput } from '../../Utils/brandForm';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import {
  appModalActionsSx,
  appModalCloseButtonSx,
  appModalHeaderDividerSx,
  appModalPaperSx,
  appModalTitleBarSx,
  appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import {
  LEADS_FORM_FIELD_GAP,
  LEADS_FORM_SECTION_GAP,
  leadsAlertSx,
  leadsGhostButtonSx,
  leadsPrimaryButtonSx,
  leadsTextFieldSx,
} from './leadsUi.styles';

const EMPTY_FORM = { name: '', phone: '', email: '', interest: '', channel: '' };

// Alto fijo del diálogo (único valor a tocar): no cambia con errores, aviso de
// duplicado ni texto largo. El formulario base (~450 px) entra sin scroll; lo
// que no entra scrollea dentro del contenido con fade y el pie queda fijo.
const LEAD_CREATE_DIALOG_HEIGHT = 'min(90vh, 500px)';

/**
 * Alta de lead (source `manual`). 409 LEAD_DUPLICATE → acción sobre el
 * existente (`onOpenExisting`; por defecto "Abrir existente").
 *
 * V3-0: solo contacto (nombre, teléfono, email, qué busca, canal). Sin
 * intereses (nacen de una consulta del form) ni etapa (la API arranca en `new`).
 *
 * @param {{ open: boolean, ownerUserId?: string|null,
 *   duplicateActionLabel?: string, onClose: () => void,
 *   onCreated: (lead: object, res: object) => void,
 *   onOpenExisting: (leadId: string) => void }} props
 */
export default function LeadCreateDialog({
  open,
  ownerUserId = null,
  duplicateActionLabel = 'Abrir existente',
  onClose,
  onCreated,
  onOpenExisting,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [duplicateId, setDuplicateId] = useState('');
  const abortRef = useRef(null);
  // Contenido con scroll (alto fijo): fade arriba / abajo si hay más.
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();
  // El aviso / la alerta del final se trae a la vista al aparecer.
  const feedbackRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setTouched(false);
    setError('');
    setDuplicateId('');
  }, [open]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const clearFeedback = () => {
    if (error) setError('');
    if (duplicateId) setDuplicateId('');
  };

  const setField = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    clearFeedback();
  };

  const setPhoneField = (e) => {
    setForm((prev) => ({ ...prev, phone: sanitizePhoneInput(e.target.value) }));
    clearFeedback();
  };

  const nameMissing = !form.name.trim();
  const contactMissing = !form.email.trim() && !form.phone.trim();
  const emailInvalid = Boolean(form.email.trim()) && !isPlausibleEmail(form.email);
  const phoneInvalid = Boolean(form.phone.trim()) && !isPlausiblePhone(form.phone);
  const invalid = nameMissing || contactMissing || emailInvalid || phoneInvalid;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setTouched(true);
    if (invalid || saving) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSaving(true);
    setError('');
    setDuplicateId('');
    try {
      const res = await createLead(
        {
          name: form.name,
          email: form.email,
          phone: form.phone,
          interest: form.interest,
          channel: form.channel,
          source: 'manual',
        },
        { ownerUserId, signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const lead = res?._httpOk ? readLead(res) : null;
      if (lead) {
        onCreated(lead, res);
        return;
      }
      const dupId = readLeadDuplicateId(res);
      if (dupId) {
        setDuplicateId(dupId);
        setError('Ya existe un lead con ese email o teléfono.');
        return;
      }
      setError(getLeadErrorMessage(res, 'No se pudo crear el lead.'));
    } catch (err) {
      if (err?.name !== 'AbortError') setError('No se pudo crear el lead.');
    } finally {
      if (abortRef.current === controller) setSaving(false);
    }
  };

  useEffect(() => {
    if (!error) return;
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    feedbackRef.current?.scrollIntoView?.({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [error]);

  // Sin helper reservado: el error aparece debajo de su campo solo si existe.
  const phoneHelper = touched && phoneInvalid
    ? 'Ingresá un teléfono válido (8–15 dígitos; +, espacios o guiones).'
    : touched && contactMissing
      ? 'Cargá al menos un teléfono o un email.'
      : '';
  const emailHelper = touched && emailInvalid
    ? 'Ingresá un email válido.'
    : touched && contactMissing
      ? 'Cargá al menos un teléfono o un email.'
      : '';

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        noValidate: true,
        sx: {
          ...appModalPaperSx,
          width: 'min(520px, 95vw)',
          maxWidth: '520px',
          // Alto fijo en todo estado (regla de modales); antes `auto` crecía
          // con el aviso, la alerta y el texto de «¿Qué busca?».
          height: LEAD_CREATE_DIALOG_HEIGHT,
          maxHeight: LEAD_CREATE_DIALOG_HEIGHT,
        },
      }}
      aria-labelledby="lead-create-title"
    >
      <DialogTitle id="lead-create-title" sx={appModalTitleBarSx}>
        <Typography component="h2" sx={appModalTitleSx}>Nuevo lead</Typography>
        <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Cerrar" sx={appModalCloseButtonSx}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <DialogContent
        ref={scrollRef}
        data-testid="lead-create-scroll"
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          px: '20px',
          pt: 2,
          pb: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: LEADS_FORM_SECTION_GAP,
          '& > *': { flexShrink: 0 },
          ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
        }}
      >
        {/* Contacto: mismos tokens que «Lead nuevo» en Nueva consulta. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: LEADS_FORM_FIELD_GAP }}>
          <TextField
            label="Nombre"
            value={form.name}
            onChange={setField('name')}
            autoFocus
            required
            fullWidth
            size="small"
            inputProps={{ maxLength: 120 }}
            error={touched && nameMissing}
            helperText={touched && nameMissing ? 'Cargá el nombre.' : ''}
            sx={leadsTextFieldSx}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: LEADS_FORM_FIELD_GAP, alignItems: 'start' }}>
            <TextField
              label="Teléfono"
              value={form.phone}
              onChange={setPhoneField}
              size="small"
              fullWidth
              autoComplete="tel"
              inputProps={{ maxLength: 40, inputMode: 'tel' }}
              error={touched && (contactMissing || phoneInvalid)}
              helperText={phoneHelper}
              sx={leadsTextFieldSx}
            />
            <TextField
              label="Email"
              value={form.email}
              onChange={setField('email')}
              size="small"
              fullWidth
              type="email"
              autoComplete="email"
              inputProps={{ maxLength: 254, inputMode: 'email' }}
              error={touched && (contactMissing || emailInvalid)}
              helperText={emailHelper}
              sx={leadsTextFieldSx}
            />
          </Box>
        </Box>
        <TextField
          label="¿Qué busca?"
          placeholder="Ej.: lote de 600 m² cerca del ingreso, financiado"
          value={form.interest}
          onChange={setField('interest')}
          size="small"
          fullWidth
          multiline
          rows={3}
          inputProps={{ maxLength: 2000 }}
          sx={leadsTextFieldSx}
        />
        <LeadChannelPicker
          value={form.channel}
          disabled={saving}
          onChange={(channel) => setForm((prev) => ({ ...prev, channel }))}
        />
        {error && duplicateId ? (
          <Box ref={feedbackRef}>
            <InfoNotice
              role="alert"
              data-testid="lead-duplicate-notice"
              title={error}
              actionLabel={duplicateActionLabel}
              onAction={() => onOpenExisting(duplicateId)}
            />
          </Box>
        ) : error ? (
          <Alert ref={feedbackRef} severity="error" variant="outlined" sx={leadsAlertSx}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={appModalActionsSx}>
        <Button variant="outlined" onClick={onClose} disabled={saving} sx={leadsGhostButtonSx}>Cancelar</Button>
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          sx={leadsPrimaryButtonSx}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
        >
          Crear lead
        </Button>
      </DialogActions>
    </Dialog>
  );
}
